import type { SettingsInput } from "./api.js";
import type { AppSettings, LabelFormat } from "../types.js";

/**
 * Validação das Configurações (SDD §10.2/§11.7 — singleton ADMIN-only). Lógica
 * PURA (sem React), testável. A microcopy espelha verbatim o schema Zod do
 * backend (apps/api/src/settings.ts). Os campos numéricos são editados como
 * texto e convertidos/validados aqui. O Seller ID + os 6 campos do endereço de
 * origem (sourceAddress*) são OBRIGATÓRIOS no form (a origem do envio é
 * cadastrada aqui, e o botão "Criar envios" depende dela), embora o backend os
 * aceite vazios para não travar o salvamento de outros campos.
 */
export interface SettingsFormValues {
  activeMarketplaceId: string;
  defaultPrepCenterName: string;
  lowStockThresholdDays: string;
  leadTimeTargetDays: string;
  inventorySyncEnabled: boolean;
  labelFormat: LabelFormat;
  amazonSellerId: string;
  sourceAddressName: string;
  sourceAddressLine1: string;
  sourceAddressCity: string;
  sourceAddressPostalCode: string;
  sourceAddressCountryCode: string;
  sourceAddressPhoneNumber: string;
}

export interface SettingsFormErrors {
  activeMarketplaceId?: string;
  defaultPrepCenterName?: string;
  lowStockThresholdDays?: string;
  leadTimeTargetDays?: string;
  amazonSellerId?: string;
  sourceAddressName?: string;
  sourceAddressLine1?: string;
  sourceAddressCity?: string;
  sourceAddressPostalCode?: string;
  sourceAddressCountryCode?: string;
  sourceAddressPhoneNumber?: string;
}

export function settingsToForm(settings: AppSettings): SettingsFormValues {
  return {
    activeMarketplaceId: settings.activeMarketplaceId,
    defaultPrepCenterName: settings.defaultPrepCenterName,
    lowStockThresholdDays: String(settings.lowStockThresholdDays),
    leadTimeTargetDays: String(settings.leadTimeTargetDays),
    inventorySyncEnabled: settings.inventorySyncEnabled,
    labelFormat: settings.labelFormat,
    amazonSellerId: settings.amazonSellerId,
    sourceAddressName: settings.sourceAddressName,
    sourceAddressLine1: settings.sourceAddressLine1,
    sourceAddressCity: settings.sourceAddressCity,
    sourceAddressPostalCode: settings.sourceAddressPostalCode,
    sourceAddressCountryCode: settings.sourceAddressCountryCode,
    sourceAddressPhoneNumber: settings.sourceAddressPhoneNumber
  };
}

/** Inteiro >= 0 editado como texto, com mensagens específicas do campo. */
function validateNonNegativeInt(
  raw: string,
  messages: { missing: string; integer: string; invalid: string }
): string | undefined {
  if (raw.trim().length === 0) {
    return messages.missing;
  }
  const value = Number(raw);
  if (Number.isNaN(value) || value < 0) {
    return messages.invalid;
  }
  if (!Number.isInteger(value)) {
    return messages.integer;
  }
  return undefined;
}

// Campos de texto obrigatórios da origem do envio (Seller ID + sourceAddress).
const REQUIRED_SOURCE_FIELDS: ReadonlyArray<
  [keyof SettingsFormValues & keyof SettingsFormErrors, string]
> = [
  ["amazonSellerId", "Informe o Seller ID da Amazon."],
  ["sourceAddressName", "Informe o nome do endereço de origem."],
  ["sourceAddressLine1", "Informe o endereço (linha 1)."],
  ["sourceAddressCity", "Informe a cidade."],
  ["sourceAddressPostalCode", "Informe o código postal."],
  ["sourceAddressCountryCode", "Informe o país (ex.: GB)."],
  ["sourceAddressPhoneNumber", "Informe o telefone (exigido pela Amazon)."]
];

export function validateSettingsForm(values: SettingsFormValues): {
  errors: SettingsFormErrors;
  valid: boolean;
  input: SettingsInput | null;
} {
  const errors: SettingsFormErrors = {};
  if (values.activeMarketplaceId.trim().length === 0) {
    errors.activeMarketplaceId = "Informe o marketplace ativo.";
  }
  if (values.defaultPrepCenterName.trim().length === 0) {
    errors.defaultPrepCenterName = "Informe o Prep Center padrão.";
  }
  const lowStockError = validateNonNegativeInt(values.lowStockThresholdDays, {
    missing: "Informe o threshold (dias).",
    integer: "Threshold deve ser inteiro.",
    invalid: "Threshold inválido."
  });
  if (lowStockError !== undefined) {
    errors.lowStockThresholdDays = lowStockError;
  }
  const leadTimeError = validateNonNegativeInt(values.leadTimeTargetDays, {
    missing: "Informe o lead time alvo (dias).",
    integer: "Lead time alvo deve ser inteiro.",
    invalid: "Lead time alvo inválido."
  });
  if (leadTimeError !== undefined) {
    errors.leadTimeTargetDays = leadTimeError;
  }
  for (const [field, message] of REQUIRED_SOURCE_FIELDS) {
    if (values[field].trim().length === 0) {
      errors[field] = message;
    }
  }

  const valid = Object.keys(errors).length === 0;

  const input: SettingsInput | null = valid
    ? {
        activeMarketplaceId: values.activeMarketplaceId.trim(),
        defaultPrepCenterName: values.defaultPrepCenterName.trim(),
        lowStockThresholdDays: Number(values.lowStockThresholdDays),
        leadTimeTargetDays: Number(values.leadTimeTargetDays),
        inventorySyncEnabled: values.inventorySyncEnabled,
        labelFormat: values.labelFormat,
        amazonSellerId: values.amazonSellerId.trim(),
        sourceAddressName: values.sourceAddressName.trim(),
        sourceAddressLine1: values.sourceAddressLine1.trim(),
        sourceAddressCity: values.sourceAddressCity.trim(),
        sourceAddressPostalCode: values.sourceAddressPostalCode.trim(),
        sourceAddressCountryCode: values.sourceAddressCountryCode.trim(),
        sourceAddressPhoneNumber: values.sourceAddressPhoneNumber.trim()
      }
    : null;

  return { errors, valid, input };
}
